import React from 'react';
import { AlertTriangle, CheckCircle2, Clock, Music } from 'lucide-react';
import { getEventDetailStatus, DETAIL_STATUS_STYLES } from '../lib/eventCompleteness';

const TAB_LABELS = { details: 'Details', setup: 'Event Setup', financials: 'Financials' };

/** Small pill: Booked (green) / Provisional (orange) / Under a week (red). */
export function DetailStatusBadge({ event, showCount = true, className = '' }) {
  const st = getEventDetailStatus(event);
  if (st.status === 'none') return null;
  const s = DETAIL_STATUS_STYLES[st.status];
  const title = st.missing.length ? `Missing: ${st.missing.map((m) => m.label).join(', ')}` : s.long;
  return (
    <span title={title} className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[11px] font-semibold whitespace-nowrap ${s.badge} ${className}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${s.dot}`} />
      {s.label}{showCount && st.missing.length > 0 ? ` · ${st.missing.length} missing` : ''}
    </span>
  );
}

/** Small coloured dot for tight spaces (diary blocks). */
export function DetailStatusDot({ event, className = '' }) {
  const st = getEventDetailStatus(event);
  if (st.status === 'none' || st.status === 'booked') return null;
  const s = DETAIL_STATUS_STYLES[st.status];
  return (
    <span
      title={st.missing.length ? `${s.long}. Missing: ${st.missing.map((m) => m.label).join(', ')}` : s.long}
      className={`inline-block w-2.5 h-2.5 rounded-full ring-2 ring-white shrink-0 ${s.dot} ${className}`}
    />
  );
}

/**
 * Panel shown in the add/edit forms above the Save button: tells reception
 * the status and exactly what is still missing, with a link to the tab.
 */
export function DetailStatusPanel({ event, isNew = false, onGoToTab }) {
  const st = getEventDetailStatus(event);
  if (st.status === 'none') return null;
  if (st.status === 'booked') {
    return (
      <div className="flex items-center gap-2 px-3 py-2 rounded-lg border border-emerald-300 bg-emerald-50 text-emerald-800 text-sm">
        <CheckCircle2 size={16} /> <b>Booked</b> – all details complete
      </div>
    );
  }
  if (st.status === 'music') {
    return (
      <div className="flex items-center gap-2 px-3 py-2 rounded-lg border border-violet-300 bg-violet-50 text-violet-800 text-sm">
        <Music size={16} /> <b>Music booking only</b> – no details required
      </div>
    );
  }
  const urgent = st.status === 'urgent';
  const s = DETAIL_STATUS_STYLES[st.status];
  const byTab = st.missing.reduce((acc, m) => { (acc[m.tab] = acc[m.tab] || []).push(m.label); return acc; }, {});
  return (
    <div className={`px-3 py-2 rounded-lg border text-sm ${urgent ? 'border-red-300 bg-red-50 text-red-900' : 'border-orange-300 bg-orange-50 text-orange-900'}`}>
      <div className="flex items-center gap-2 font-semibold">
        {urgent ? <AlertTriangle size={16} /> : <Clock size={16} />}
        {s.long}
      </div>
      <p className="text-xs mt-0.5 opacity-90">
        {urgent
          ? (isNew ? 'Within 7 days of the event – this booking cannot be saved until everything below is filled in.' : 'Within 7 days of the event – kitchen and operations need these now. You can save partial updates but it stays red until complete.')
          : 'Can be saved now. Reception will get a task to complete these 7 days before the event.'}
      </p>
      <ul className="mt-1.5 space-y-0.5">
        {Object.entries(byTab).map(([tab, labels]) => (
          <li key={tab} className="text-xs">
            {onGoToTab ? (
              <button type="button" onClick={() => onGoToTab(tab)} className="font-semibold underline underline-offset-2 mr-1">{TAB_LABELS[tab] || tab}:</button>
            ) : (
              <span className="font-semibold mr-1">{TAB_LABELS[tab] || tab}:</span>
            )}
            {labels.join(', ')}
          </li>
        ))}
      </ul>
    </div>
  );
}
